import React, { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import type { Category } from '../../types';
import { categoryService } from '../../services/category.service';
import { locationService, type City } from '../../services/location.service';
import { getCategoryIcon } from '../../utils/categoryIcons';
import { PrivateLink } from '../common/PrivateLink';

/**
 * The home hero is a sentence the visitor completes: "J'ai besoin d'un pro en [Plomberie] à [Casablanca]".
 * Both blanks are listboxes shared by the two actions: publishing a request (pre-filled with those answers)
 * or browsing the artisans that match them.
 */

type Option = { id: string; label: string; icon?: React.ReactNode };

interface InlineSelectProps {
    label: string;
    /** Text shown in the sentence while nothing is chosen ("quel métier ?"). */
    placeholder: string;
    /** First entry of the list, which clears the choice ("tous les métiers"). */
    anyLabel: string;
    value: Option | null;
    options: Option[];
    onChange: (option: Option | null) => void;
    searchPlaceholder?: string;
}

const OPTION_CLASS = 'flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-start text-sm outline-none focus-visible:bg-muted';

const InlineSelect: React.FC<InlineSelectProps> = ({ label, placeholder, anyLabel, value, options, onChange, searchPlaceholder }) => {
    const [open, setOpen] = useState(false);
    // The list hangs from the trigger's start edge, or from its end edge when it would leave the viewport (phones).
    const [alignEnd, setAlignEnd] = useState(false);
    const [filter, setFilter] = useState('');
    const root = useRef<HTMLSpanElement>(null);
    const trigger = useRef<HTMLButtonElement>(null);
    const list = useRef<HTMLUListElement>(null);
    const listId = useId();

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (event: MouseEvent) => {
            // Outside click: close without stealing the focus from what was clicked.
            if (!root.current?.contains(event.target as Node)) { setOpen(false); setFilter(''); }
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    const close = () => {
        setOpen(false);
        setFilter('');
        // The focused option unmounts with the list: give the focus back to the trigger.
        trigger.current?.focus();
    };
    const choose = (option: Option | null) => { onChange(option); close(); };
    const visible = filter
        ? options.filter((option) => option.label.toLocaleLowerCase().includes(filter.toLocaleLowerCase()))
        : options;

    // Arrow keys move between options; Enter and Space choose; Escape closes (handled on the root).
    const onOptionKeyDown = (event: React.KeyboardEvent<HTMLLIElement>, option: Option | null) => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); choose(option); return; }
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        event.preventDefault();
        const items = [...(event.currentTarget.parentElement?.querySelectorAll<HTMLLIElement>('[role="option"]') ?? [])];
        const index = items.indexOf(event.currentTarget);
        const next = items[event.key === 'ArrowDown' ? index + 1 : index - 1];
        next?.focus();
    };

    // From the search box: Enter picks the first match, ArrowDown steps into the list.
    const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            if (event.nativeEvent.isComposing) return;
            event.preventDefault();
            if (filter && visible.length > 0) choose(visible[0]);
            return;
        }
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        const items = list.current?.querySelectorAll<HTMLLIElement>('[role="option"]');
        items?.[filter ? 1 : 0]?.focus();
    };

    const renderOption = (option: Option | null) => {
        const selected = option === null ? value === null : value?.id === option.id;
        return (
            <li
                key={option?.id ?? 'none'}
                role="option"
                aria-selected={selected}
                tabIndex={0}
                onClick={() => choose(option)}
                onKeyDown={(event) => onOptionKeyDown(event, option)}
                className={`${OPTION_CLASS} ${selected ? 'bg-primary/8 text-primary' : 'hover:bg-muted'}`}
            >
                {option?.icon && <span className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground">{option.icon}</span>}
                <span className="flex-1 truncate">{option ? option.label : anyLabel}</span>
                {selected && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
            </li>
        );
    };

    return (
        <span
            ref={root}
            className="relative inline-block align-baseline"
            onKeyDown={(event) => { if (event.key === 'Escape') { event.stopPropagation(); close(); } }}
        >
            <button
                ref={trigger}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={open ? listId : undefined}
                aria-label={`${label} : ${value ? value.label : placeholder}`}
                onClick={() => {
                    const rect = root.current?.getBoundingClientRect();
                    if (rect) setAlignEnd(rect.left + 288 > window.innerWidth - 16 && rect.right - 288 >= 0);
                    setOpen((state) => !state);
                }}
                className={`group inline-flex max-w-[16rem] items-baseline gap-1 border-b-[3px] pb-0.5 text-start transition-colors hover:border-primary focus-visible:border-primary sm:max-w-none ${value ? 'border-primary text-primary' : 'border-foreground/25 text-foreground/60'}`}
            >
                <span className="truncate">{value ? value.label : placeholder}</span>
                <ChevronDown className={`h-[0.6em] w-[0.6em] shrink-0 self-center transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>

            {open && (
                <div className={`absolute top-full z-50 mt-3 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-border bg-card text-base font-medium text-foreground shadow-xl ${alignEnd ? 'end-0' : 'start-0'}`}>
                    {searchPlaceholder && (
                        <div className="border-b border-border p-2">
                            <input
                                type="search"
                                value={filter}
                                onChange={(event) => setFilter(event.target.value)}
                                onKeyDown={onSearchKeyDown}
                                placeholder={searchPlaceholder}
                                aria-label={searchPlaceholder}
                                className="h-10 w-full rounded-xl bg-muted px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                            />
                        </div>
                    )}
                    <ul ref={list} id={listId} role="listbox" aria-label={label} className="max-h-72 overflow-y-auto py-1">
                        {renderOption(null)}
                        {visible.map((option) => renderOption(option))}
                    </ul>
                </div>
            )}
        </span>
    );
};

export const HeroComposer: React.FC = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    // First render uses the prerendered catalogue and cities (same data as the built HTML); both refresh on mount.
    const [categories, setCategories] = useState<Category[]>(() => categoryService.peekAllCategories() ?? []);
    const [cities, setCities] = useState<City[]>(() => locationService.peekAllCities() ?? []);
    const [category, setCategory] = useState<Option | null>(null);
    const [city, setCity] = useState<Option | null>(null);

    useEffect(() => {
        let cancelled = false;
        Promise.all([categoryService.getAllCategories(), locationService.getAllCities()])
            .then(([loadedCategories, loadedCities]) => {
                if (cancelled) return;
                setCategories(loadedCategories ?? []);
                setCities(loadedCities ?? []);
            })
            .catch((error) => console.error('Failed to load search data', error));
        return () => { cancelled = true; };
    }, []);

    const arabic = ['ar', 'darija_ar'].includes(i18n.language);
    const categoryOptions: Option[] = categories.map((entry) => ({
        id: entry.id,
        label: entry.names?.[i18n.language] || entry.names?.fr || entry.slug,
        icon: getCategoryIcon(entry.slug, entry.iconUrl, 'w-4 h-4'),
    }));
    const cityOptions: Option[] = cities.map((entry) => ({ id: entry.id, label: (arabic && entry.nameAr) || entry.name }));

    const selectedCity = city ? cities.find((entry) => entry.id === city.id) : undefined;
    const withQuery = (path: string, params: URLSearchParams) => {
        const query = params.toString();
        return query ? `${path}?${query}` : path;
    };
    // Both actions carry the same two answers; each page reads the parameters it understands.
    const publish = () => {
        const params = new URLSearchParams();
        if (category) params.set('categoryId', category.id);
        if (selectedCity) {
            params.set('city', selectedCity.name);
            params.set('lat', String(selectedCity.lat));
            params.set('lon', String(selectedCity.lon));
        }
        navigate(withQuery('/jobs/create', params));
    };
    const browseParams = new URLSearchParams();
    if (category) browseParams.set('categoryId', category.id);
    if (selectedCity) browseParams.set('cityId', selectedCity.id);
    const browseTo = withQuery('/search', browseParams);

    return (
        <div className="text-foreground">
            <p className="text-2xl font-semibold leading-[1.6] sm:text-3xl md:text-[2rem] lg:text-4xl lg:leading-[1.55]">
                <span className="inline-flex flex-wrap items-baseline gap-x-[0.35em] gap-y-2">
                    <span>{t('home.composerLead')}</span>
                    <InlineSelect
                        label={t('home.composerTrade')}
                        placeholder={t('home.composerTradePlaceholder')}
                        anyLabel={t('home.composerAnyTrade')}
                        value={category}
                        options={categoryOptions}
                        onChange={setCategory}
                    />
                    <span>{t('home.composerAt')}</span>
                    <InlineSelect
                        label={t('home.composerCity')}
                        placeholder={t('home.composerCityPlaceholder')}
                        anyLabel={t('home.composerAnyCity')}
                        value={city}
                        options={cityOptions}
                        onChange={setCity}
                        searchPlaceholder={t('home.composerSearchCity')}
                    />
                </span>
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                <button
                    type="button"
                    onClick={publish}
                    data-track="hero_post_request"
                    className="inline-flex h-14 items-center justify-center rounded-2xl bg-primary px-8 text-lg font-semibold text-primary-foreground shadow-[0_12px_30px_-12px_rgba(33,84,255,0.7)] transition-colors hover:bg-primary/90"
                >
                    {t('home.composerCta')}
                </button>
                <PrivateLink
                    to={browseTo}
                    data-track="hero_browse_providers"
                    className="inline-flex h-14 items-center justify-center rounded-2xl border border-foreground/15 bg-card px-8 text-lg font-semibold transition-colors hover:border-foreground/40"
                >
                    {t('home.composerBrowse')}
                </PrivateLink>
            </div>
        </div>
    );
};
